import {
  ExternalLink,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Printer,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ProviderDirectoryV2ContactPoint } from '@/services/types/provider-directory-v2-types';

interface ContactMethodCardProps {
  contact: ProviderDirectoryV2ContactPoint;
  onEdit?: () => void;
  className?: string;
}

const POINT_TYPE_ICONS: Record<string, typeof Phone> = {
  PHONE: Phone,
  FAX: Printer,
  EMAIL: Mail,
  WEBSITE: Globe,
  PORTAL: Globe,
  MAILING_ADDRESS: MapPin,
};

/** Colors for the 36px icon square and type badge text */
const POINT_TYPE_STYLES: Record<
  string,
  { iconBg: string; iconColor: string; badgeBg: string; badgeColor: string }
> = {
  FAX: {
    iconBg: 'bg-[#EDFFF0]',
    iconColor: 'text-success',
    badgeBg: 'bg-[#EDFFF0]',
    badgeColor: 'text-success',
  },
  PHONE: {
    iconBg: 'bg-[#EDFFF0]',
    iconColor: 'text-success',
    badgeBg: 'bg-[#EDFFF0]',
    badgeColor: 'text-success',
  },
  EMAIL: {
    iconBg: 'bg-[#FFF3ED]',
    iconColor: 'text-warning',
    badgeBg: 'bg-[#FFF3ED]',
    badgeColor: 'text-warning',
  },
  WEBSITE: {
    iconBg: 'bg-[#F1EFFF]',
    iconColor: 'text-primary',
    badgeBg: 'bg-[#F1EFFF]',
    badgeColor: 'text-primary',
  },
  PORTAL: {
    iconBg: 'bg-[#F1EFFF]',
    iconColor: 'text-primary',
    badgeBg: 'bg-[#F1EFFF]',
    badgeColor: 'text-primary',
  },
  MAILING_ADDRESS: {
    iconBg: 'bg-zinc-100',
    iconColor: 'text-zinc-500',
    badgeBg: 'bg-zinc-100',
    badgeColor: 'text-zinc-600',
  },
};

const DEFAULT_STYLE = {
  iconBg: 'bg-zinc-100',
  iconColor: 'text-zinc-500',
  badgeBg: 'bg-zinc-100',
  badgeColor: 'text-zinc-600',
};

const PURPOSE_STYLES: Record<string, { bg: string; color: string }> = {
  RECORDS: { bg: 'bg-[#F4F4F5]', color: 'text-zinc-600' },
  BILLING: { bg: 'bg-[#F4F4F5]', color: 'text-zinc-600' },
  GENERAL: { bg: 'bg-[#F4F4F5]', color: 'text-zinc-600' },
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  CALLED_PROVIDER: 'called provider',
  PROVIDER_WEBSITE: 'provider website',
  DOCUMENT: 'document',
  OTHER: 'other',
};

const formatAddress = (c: ProviderDirectoryV2ContactPoint) => {
  const parts = [
    c.address_line1,
    c.address_line2,
    c.city,
    c.state,
    c.postal_code,
  ].filter(Boolean);
  return parts.length > 0
    ? parts.join(', ')
    : (c.formatted_address ?? c.value ?? null);
};

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} months ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const getContactHref = (c: ProviderDirectoryV2ContactPoint) => {
  if (!c.value) return null;
  if (c.point_type === 'PHONE' || c.point_type === 'FAX')
    return `tel:${c.value}`;
  if (c.point_type === 'EMAIL') return `mailto:${c.value}`;
  if (c.point_type === 'WEBSITE' || c.point_type === 'PORTAL')
    return c.value;
  return null;
};

export const ContactMethodCard = ({
  contact,
  onEdit,
  className,
}: ContactMethodCardProps) => {
  const Icon = POINT_TYPE_ICONS[contact.point_type] ?? Phone;
  const typeStyle =
    POINT_TYPE_STYLES[contact.point_type] ?? DEFAULT_STYLE;
  const displayValue =
    contact.point_type === 'MAILING_ADDRESS'
      ? formatAddress(contact)
      : contact.value;
  const href = getContactHref(contact);
  const purposeStyle = PURPOSE_STYLES[contact.purpose ?? ''];

  // Build provenance line
  const provenanceParts: string[] = [];
  if (contact.verified_at) {
    const ago = formatRelativeTime(contact.verified_at);
    if (ago) provenanceParts.push(`Verified ${ago}`);
  } else if (contact.captured_at) {
    const ago = formatRelativeTime(contact.captured_at);
    if (ago) provenanceParts.push(`Added ${ago}`);
  }
  if (contact.source_type) {
    const src =
      SOURCE_TYPE_LABELS[contact.source_type] ??
      contact.source_type.replace(/_/g, ' ').toLowerCase();
    provenanceParts.push(`Source: ${src}`);
  }
  if (contact.captured_by_agent) {
    provenanceParts.push(`Captured by ${contact.captured_by_agent}`);
  }

  // Verification state
  const isVerified =
    contact.verification_status === 'VERIFIED' || Boolean(contact.verified_at);
  const isPending = contact.verification_status === 'PENDING';

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-background p-4 flex flex-col gap-2',
        className,
      )}
    >
      {/* Header: icon square + type badge + purpose + preferred + verification */}
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            typeStyle.iconBg,
          )}
        >
          <Icon size={15} className={typeStyle.iconColor} />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {/* Type badge */}
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.03em]',
              typeStyle.badgeBg,
              typeStyle.badgeColor,
            )}
          >
            {(contact.point_type ?? 'OTHER').replace(/_/g, ' ')}
          </span>

          {/* Purpose badge */}
          {contact.purpose && purposeStyle && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-medium',
                purposeStyle.bg,
                purposeStyle.color,
              )}
            >
              {contact.purpose
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (m) => m.toUpperCase())}
            </span>
          )}

          {/* Preferred badge */}
          {contact.is_preferred && (
            <span className="inline-flex items-center rounded-full bg-[#F1EFFF] px-2 py-[2px] text-[10px] font-medium text-primary">
              Preferred
            </span>
          )}

          {/* Verification text */}
          {isVerified && (
            <span className="text-[10px] font-medium text-success">
              Verified
            </span>
          )}
          {isPending && (
            <span className="text-[10px] font-medium text-warning">
              Pending
            </span>
          )}
        </div>

        {/* Edit icon */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="shrink-0 p-0.5 text-zinc-500 hover:text-info-dark rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Edit contact"
          >
            <Pencil size={14} />
          </button>
        )}
      </div>

      {/* Value */}
      {displayValue && (
        <div className="pl-[42px]">
          {href ? (
            <a
              href={href}
              target={
                contact.point_type === 'WEBSITE' ||
                contact.point_type === 'PORTAL'
                  ? '_blank'
                  : undefined
              }
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-info-dark hover:text-primary hover:underline break-words"
            >
              {displayValue}
              {(contact.point_type === 'WEBSITE' ||
                contact.point_type === 'PORTAL') && (
                <ExternalLink size={12} className="shrink-0" />
              )}
            </a>
          ) : (
            <span className="text-sm font-medium text-info-dark break-words">
              {displayValue}
            </span>
          )}
        </div>
      )}

      {/* Provenance line */}
      {provenanceParts.length > 0 && (
        <p className="pl-[42px] text-[11px] text-zinc-500">
          {provenanceParts.join(' · ')}
        </p>
      )}
    </div>
  );
};
